import { infiniteQueryOptions, QueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  OPEN_ORDER_STATUSES,
  ORDER_STOP_TYPES,
  WS_INVALIDATED_QUERY_STALE_TIME,
} from '../constants';
import { operations, SchemaOrderDto } from '../generated/apiTypes';
import { dataOrThrow, fetchClient } from '../lib/apiClient';
import { Gwei } from '../lib/gwei';
import { removeUndefined } from '../lib/objectHelpers';
import { selectFirstStopOrders } from '../lib/orderHelpers';
import { invalidateQueries } from '../lib/reactQueryHelpers';
import { PrettyMerge } from '../lib/tsUtilityTypes';
import { useCancelOrderStore } from './useCancelledOrdersStore';
import { useFlattenedPages } from './useFlattenedPages';

export const transformOrder = (order: SchemaOrderDto) => ({
  ...order,
  quantity: Gwei.toGwei(order.quantity),
  price: Gwei.toGwei(order.price),
  filled: Gwei.toGwei(order.filled),
  availableQuantity: Gwei.toGwei(order.availableQuantity),
  stopPrice: Gwei.toGwei(order.stopPrice),
  reduceOnly: order.reduceOnly,
});

export type Order = ReturnType<typeof transformOrder>;

export const OrderEntities = {
  default: 'default',
  openOrders: 'openOrders',
  positionStopOrders: 'positionStopOrders',
} as const;
type OrderEntitiesType = (typeof OrderEntities)[keyof typeof OrderEntities];

export type OrderParams = PrettyMerge<
  Omit<operations['OrderController_listBySubaccountId']['parameters']['query'], 'cursor'>,
  {
    subaccountId: string | undefined;
    orderEntity?: (typeof OrderEntities)[keyof typeof OrderEntities];
  }
>;

export const orderQuery = ({ orderEntity, subaccountId, ...params }: OrderParams) =>
  infiniteQueryOptions({
    queryKey: ['orders', { subaccountId }, orderEntity, params] as const,

    initialPageParam: '',
    queryFn: async ({ pageParam, signal }) => {
      if (!subaccountId) return null;
      const { data } = await fetchClient
        .GET('/v1/order', {
          signal,
          params: {
            query: removeUndefined({
              subaccountId,
              ...params,
              cursor: pageParam || undefined,
            }),
          },
        })
        .then(dataOrThrow);
      return { orders: data?.data.map(transformOrder) ?? null, nextCursor: data?.nextCursor };
    },

    enabled: Boolean(subaccountId),
    staleTime: WS_INVALIDATED_QUERY_STALE_TIME,
    getNextPageParam: (lastPage) => lastPage?.nextCursor,
  });

export const useOrders = (params: Omit<OrderParams, 'orderEntity'>) =>
  useInfiniteQuery(
    orderQuery({
      orderEntity: OrderEntities.default,
      ...params,
    })
  );

export const useOpenOrders = (params: Omit<OrderParams, 'statuses' | 'orderEntity'>) => {
  const cancelledOrdersById = useCancelOrderStore((s) => s.cancelledOrdersById);
  return useInfiniteQuery({
    ...orderQuery({
      orderEntity: OrderEntities.openOrders,
      statuses: OPEN_ORDER_STATUSES,
      close: false,
      ...params,
    }),
    select: (data) => ({
      pages: data.pages.map((page) => ({
        ...page,
        orders:
          page?.orders?.filter((o) => {
            if (o.type === 'MARKET' && o.status === 'PENDING') return true;
            if (cancelledOrdersById[o.id]) return false;
            return o.type === 'LIMIT';
          }) ?? [],
      })),
    }),
  });
};

const positionStopOrdersQuery = ({
  subaccountId,
  productId,
}: {
  subaccountId: string | undefined;
  productId: string | undefined;
}) =>
  orderQuery({
    orderEntity: OrderEntities.positionStopOrders,
    subaccountId,
    productIds: productId ? [productId] : undefined,
    statuses: ['SUBMITTED', 'PENDING'],
    close: true,
    stopTypes: Object.values(ORDER_STOP_TYPES),
  });

export const usePositionStopOrdersByProductId = ({
  subaccountId,
  productId,
}: {
  subaccountId: string | undefined;
  productId: string | undefined;
}) =>
  useInfiniteQuery({
    ...positionStopOrdersQuery({ subaccountId, productId }),
    select: (data) =>
      selectFirstStopOrders(data?.pages.map((page) => page?.orders || []).flat() ?? []),
  });

export const invalidateOrders = (
  queryClient: QueryClient,
  subaccountId: string,
  orderEntity?: OrderEntitiesType
) => {
  const queryKey = ['orders', { subaccountId }, orderEntity] as const;
  invalidateQueries(queryClient, queryKey);
};

export const useFlattenedOrders = (pages?: { orders: Order[] }[]) =>
  useFlattenedPages('orders', pages);
